import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box } from '@chakra-ui/react';
import {
    ColumnDef,
    ColumnFiltersState,
    ColumnSort,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import { Pagination } from '../../../widgets/Table/Pagination';
import { Table } from '../../../widgets/Table/Table';
import { TableControls } from '../../../widgets/Table/TableControls';
import { useClientFilterableColumns } from '../../../widgets/Table/useClientFilterableColumns';
import { useTableColumnSettings } from '../../../widgets/Table/useTableColumnSettings';

export const DetailsBlock = <T,>({
    data,
    columns,
    columnSettingsKey,
    title,
    initialSort,
    paginate,
}: {
    data: T[];
    columns: ColumnDef<T, any>[];
    columnSettingsKey: string;
    title: string;
    initialSort?: ColumnSort | ColumnSort[];
    paginate?: boolean;
}) => {
    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } =
        useTableColumnSettings(columnSettingsKey);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const filterableColumns = useClientFilterableColumns(columns);

    const table = useReactTable({
        data,
        columns: filterableColumns,
        state: {
            columnVisibility,
            columnOrder,
            columnFilters,
        },
        initialState: {
            sorting: initialSort ? (Array.isArray(initialSort) ? initialSort : [initialSort]) : undefined,
            pagination: paginate
                ? {
                      pageIndex: 0,
                      pageSize: 50,
                  }
                : undefined,
        },
        sortDescFirst: false,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: paginate ? getPaginationRowModel() : undefined,
    });

    return (
        <AccordionItem isDisabled={data.length === 0}>
            {({ isExpanded }) => (
                <>
                    <AccordionButton background="whiteAlpha.50">
                        <Box as="span" flex="1" textAlign="left">
                            {title} ({data.length})
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                        {/* Chakra keeps collapsed panels mounted, so without this guard every table re-renders*/}
                        {isExpanded && (
                            <>
                                <TableControls
                                    table={table}
                                    exportCsvName={columnSettingsKey}
                                    onReset={() => setColumnFilters([])}
                                />
                                <Table table={table} allowXOverflow />
                                {paginate && <Pagination table={table} />}
                            </>
                        )}
                    </AccordionPanel>
                </>
            )}
        </AccordionItem>
    );
};
